import React, { useState, useEffect } from 'react';
import '../css/Reviews.css';

// Shuffles an array into a random order (Fisher–Yates algorithm).
// Returns a NEW array without mutating the original.
const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

// Stars for a rating, kept between 0 and 5 (handles strings and decimals)
const renderStars = (rating) => {
    const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
};

const ReviewsCarousel = () => {
    const [current, setCurrent] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paused, setPaused] = useState(false);

    const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fetch reviews from JSON (public folder)
    useEffect(() => {
        fetch('/json/reviews.json')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load reviews');
                return res.json();
            })
            .then((data) => {
                setReviews(shuffleArray(data)); // reshuffle every load
                setLoading(false);
            })
            .catch((err) => {
                console.warn('Reviews:', err.message);
                setError(err.message);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const total = reviews.length;
    const visibleCount = windowWidth < 768 ? 1 : 2;

    // Auto-advance. Only runs with 2+ reviews (the old version divided by zero while
    // loading), and stops while hovered/focused or if the visitor prefers reduced motion.
    useEffect(() => {
        if (total < 2 || paused || prefersReducedMotion) return;
        const interval = setInterval(() => {
            setCurrent((c) => (c + 1) % total);
        }, 7000);
        return () => clearInterval(interval);
    }, [total, paused, prefersReducedMotion]);

    const prev = () => setCurrent((c) => (c - 1 + total) % total);
    const next = () => setCurrent((c) => (c + 1) % total);

    // Nothing to show: render nothing (no "Loading..." or error text for visitors/crawlers)
    if (loading || error || total === 0) return null;

    // Repeat the first slides at the end so the loop looks continuous
    const displayedReviews = total > 1
        ? reviews.concat(reviews.slice(0, visibleCount))
        : reviews;

    return (
        <section
            className="reviews-carousel-container"
            aria-roledescription="carousel"
            aria-label="Fan reviews"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <h2 className="reviews-carousel-title">What Fans Are Saying</h2>

            <div
                className="reviews-carousel-slider"
                aria-live={paused ? 'polite' : 'off'}
                style={{ transform: `translateX(-${current * (100 / visibleCount)}%)` }}
            >
                {displayedReviews.map((r, idx) => (
                    <div
                        key={idx}
                        className="review-slide"
                        style={{ flexBasis: `${100 / visibleCount}%` }}
                        aria-hidden={idx >= total ? true : undefined} // repeated slides
                    >
                        <article className="review-card">
                            {r.image && (
                                <img
                                    src={r.image}
                                    alt={`Photo of ${r.name}`}
                                    className="reviewer-image"
                                    width="80"
                                    height="80"
                                    loading="lazy"
                                />
                            )}
                            <p className="reviewer-name">{r.name}</p>
                            <div
                                className="review-stars"
                                role="img"
                                aria-label={`${r.rating} out of 5 stars`}
                            >
                                {renderStars(r.rating)}
                            </div>
                            <blockquote className="review-text">{r.review}</blockquote>
                        </article>
                    </div>
                ))}
            </div>

            {total > 1 && (
                <div className="reviews-carousel-nav">
                    <button onClick={prev} className="carousel-btn prev-btn" aria-label="Previous review">‹</button>
                    <button onClick={next} className="carousel-btn next-btn" aria-label="Next review">›</button>
                </div>
            )}
        </section>
    );
};

export default ReviewsCarousel;